export const card = {
    position: 'relative',
    width: '100%',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.015) 100%)',
    border: '1px solid rgba(187, 188, 188, 0.12)',
    borderRadius: '22px',
    overflow: 'hidden',
    transition: 'transform 0.25s ease, border-color 0.25s ease, box-shadow 0.35s ease',
    '&:hover': {
        transform: 'translateY(-3px)',
        borderColor: 'rgba(0, 215, 255, 0.35)',
        boxShadow: '0 24px 48px -22px rgba(0, 215, 255, 0.35)',
    },
};

const coverBase = {
    height: { xs: 224, md: 256, lg: 288 },
    position: 'relative',
    overflow: 'hidden',
};

const coverAfter = {
    content: '""',
    position: 'absolute',
    inset: 0,
    zIndex: 1,
};

// Cover photo: dark bands top + bottom so pills and publisher info are readable
export const cover = {
    ...coverBase,
    '&::after': {
        ...coverAfter,
        background: 'linear-gradient(180deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.75) 18%, rgba(0,0,0,0.05) 26%, rgba(0,0,0,0.05) 65%, rgba(0,0,0,0.75) 73%, rgba(0,0,0,0.75) 100%)',
    },
};

// Color-only (no cover photo): original bottom-only gradient
export const coverColor = {
    ...coverBase,
    '&::after': {
        ...coverAfter,
        background: 'linear-gradient(180deg, rgba(0,0,0,0) 20%, rgba(0,0,0,0.55) 60%, rgba(0,0,0,0.92) 100%)',
    },
};

// 3 white semi-transparent stripes — match prototype card cover exactly
const stripeBase = {
    position: 'absolute',
    borderRadius: '999px',
    
    backgroundColor: 'rgba(255,255,255,0.18)',
};

export const coverStripes = {
    wrapper: {
        position: 'absolute',
        inset: 0,
        zIndex: 0,
        overflow: 'hidden',
    },
    bar1: { ...stripeBase, left: '-5%', top: '13%', width: '70%', height: '10%' },
    bar2: { ...stripeBase, left: '25%', top: '29%', width: '80%', height: '5%'  },
    bar3: { ...stripeBase, left: '10%', top: '40%', width: '52%', height: '10%' },
};

export const coverTopLeft = {
    position: 'absolute',
    top: 12,
    left: 12,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 1,
    zIndex: 2,   // above ::after overlay (zIndex 1)
};

// Fix 1 — zIndex 2 ensures logo renders above the ::after gradient overlay
export const coverBottom = {
    position: 'absolute',
    bottom: 12,
    left: 16,
    right: 16,
    display: 'flex',
    alignItems: 'flex-end',
    gap: 1.5,
    zIndex: 2,   // above ::after overlay (zIndex 1)
};

export const publisherLogo = {
    height: 48,
    width: 48,
    borderRadius: '12px',
    boxShadow: '0 18px 40px -18px rgba(0,0,0,0.6), 0 4px 14px -8px rgba(0,120,255,0.25)',
    flexShrink: 0,
    objectFit: 'cover',
};

export const publisherName = {
    color: 'var(--ecal-white)',
    fontWeight: 600,
    fontSize: { xs: '1rem', md: '1.125rem' },
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
};

export const publisherTimezone = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    fontSize: '0.72rem',
    color: 'rgba(255,255,255,0.85)',
};

export const body = {
    p: { xs: 2, md: 2.5 },
};

export const nextEventLabel = {
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    letterSpacing: '0.6px',   // fix 4 — was 0.9216px (0.08em)
    color: 'var(--ecal-lightgrey)',
};

export const nextEventName = {
    fontSize: { xs: '0.875rem', md: '1rem' },
    fontWeight: 500,
    color: 'var(--ecal-white)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    mt: 0.25,
};

export const countdown = {
    fontSize: '0.72rem',
    color: 'var(--ecal-lightblue)',
    mt: 0.5,
};

export const actions = {
    mt: 2,
    display: 'flex',
    alignItems: 'center',
    gap: 1,
};

export const syncButton = {
    flex: 1,
    fontSize: '16px',   // fix 2 — was 14px
};

// Gradient border wrapper for the share button
export const shareWrapper = {
    background: 'linear-gradient(90deg, #00D7FF, #0078FF)',
    borderRadius: '999px',
    padding: '1.5px',
    display: 'inline-flex',
};

export const shareButton = {
    minWidth: 0,
    px: '17.6px',
    py: '7px',
    borderRadius: '999px',
    border: 'none',
    background: 'var(--ecal-navy)',
    color: 'var(--ecal-white)',
    position: 'relative',
    '&:hover': { background: 'var(--ecal-navy)', border: 'none' },
    '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        padding: '1.5px',
        borderRadius: '999px',
        background: 'linear-gradient(90deg, #00D7FF, #0078FF)',
        WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
        WebkitMaskComposite: 'xor',
        maskComposite: 'exclude',
        pointerEvents: 'none',
    },
};

// Pills
export const pill = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    px: 0.75,
    py: 0.25,
    borderRadius: '999px',
    fontSize: '0.72rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.12)',
    color: 'var(--ecal-white)',
};

export const pillHot = {
    ...pill,
    padding: '3px 9px',   // fix 6 — was 2px 6px
    background: 'linear-gradient(90deg, #FFDF10, #FF4D00)',
    color: '#1a0a00',
    border: 'none',
};
